/**
 * Help Center API client.
 * Calls vttd-help backend directly (cross-origin, shared Auth0 tenant).
 */

const HC_API_BASE = import.meta.env.VITE_HELP_CENTER_API_URL || '';

async function request(url, accessToken, options = {}) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Remove Content-Type for FormData (browser sets multipart boundary)
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const response = await fetch(`${HC_API_BASE}${url}`, {
    ...options,
    headers,
    body:
      options.body instanceof FormData
        ? options.body
        : options.body
          ? JSON.stringify(options.body)
          : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// --- Analytics ---

export function fetchOverview(token) {
  return request('/api/admin/analytics/overview', token);
}

export function fetchPopularArticles(token) {
  return request('/api/admin/analytics/popular-articles', token);
}

export function fetchTicketStats(token) {
  return request('/api/admin/analytics/ticket-stats', token);
}

export function fetchTopSearchTerms(token) {
  return request('/api/search/admin/top-terms', token);
}

// --- Articles ---

export function fetchArticles(token, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/articles/admin/all${qs ? `?${qs}` : ''}`, token);
}

export function fetchArticle(token, id) {
  return request(`/api/articles/admin/${encodeURIComponent(id)}`, token);
}

export function createArticle(token, data) {
  return request('/api/articles', token, { method: 'POST', body: data });
}

export function updateArticle(token, id, data) {
  return request(`/api/articles/admin/${encodeURIComponent(id)}`, token, {
    method: 'PUT',
    body: data,
  });
}

export function publishArticle(token, id, status) {
  return request(`/api/articles/admin/${encodeURIComponent(id)}/publish`, token, {
    method: 'PATCH',
    body: { status },
  });
}

export function deleteArticle(token, id) {
  return request(`/api/articles/admin/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}

// --- Categories ---

export function fetchCategories(token) {
  return request('/api/categories/admin/all', token);
}

export function createCategory(token, data) {
  return request('/api/categories', token, { method: 'POST', body: data });
}

export function updateCategory(token, id, data) {
  return request(`/api/categories/${encodeURIComponent(id)}`, token, {
    method: 'PUT',
    body: data,
  });
}

export function deleteCategory(token, id) {
  return request(`/api/categories/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}

export function reorderCategory(token, id, order) {
  return request(`/api/categories/${encodeURIComponent(id)}/reorder`, token, {
    method: 'PATCH',
    body: { displayOrder: order },
  });
}

// --- Tickets ---

export function fetchTickets(token, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/tickets/admin/all${qs ? `?${qs}` : ''}`, token);
}

export function fetchTicket(token, id) {
  return request(`/api/tickets/admin/${encodeURIComponent(id)}`, token);
}

export function updateTicket(token, id, data) {
  return request(`/api/tickets/admin/${encodeURIComponent(id)}`, token, {
    method: 'PATCH',
    body: data,
  });
}

export function deleteTicket(token, id) {
  return request(`/api/tickets/admin/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}

// --- Feature Requests ---

export function fetchFeatureRequests(token, params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/api/feature-requests/admin/all${qs ? `?${qs}` : ''}`, token);
}

export function fetchFeatureRequest(token, id) {
  return request(`/api/feature-requests/admin/${encodeURIComponent(id)}`, token);
}

export function updateFeatureRequest(token, id, data) {
  return request(`/api/feature-requests/admin/${encodeURIComponent(id)}`, token, {
    method: 'PATCH',
    body: data,
  });
}

export function deleteFeatureRequest(token, id) {
  return request(`/api/feature-requests/admin/${encodeURIComponent(id)}`, token, {
    method: 'DELETE',
  });
}

// --- FAQs ---

export function fetchFAQs(token) {
  return request('/api/faqs/admin/all', token);
}

export function createFAQ(token, data) {
  return request('/api/faqs', token, { method: 'POST', body: data });
}

export function updateFAQ(token, id, data) {
  return request(`/api/faqs/admin/${encodeURIComponent(id)}`, token, {
    method: 'PUT',
    body: data,
  });
}

export function deleteFAQ(token, id) {
  return request(`/api/faqs/admin/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}

export function reorderFAQs(token, items) {
  return request('/api/faqs/admin/reorder', token, { method: 'PATCH', body: { items } });
}

// --- Media ---

export function fetchMedia(token) {
  return request('/api/admin/media', token);
}

export function uploadMedia(token, file) {
  const formData = new FormData();
  formData.append('file', file);
  return request('/api/admin/media/upload', token, { method: 'POST', body: formData });
}

export function deleteMedia(token, id) {
  return request(`/api/admin/media/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}
